import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { FormDialog, FormField } from "../form";
import { buildPhotoUrl } from "../../utils/photoUrl";
import { VENDOR_CATEGORIES, VENDOR_STATUSES } from "../../constants";

const WEEKDAY_OPTIONS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const TAG_OPTIONS = [
  "Fried Chicken",
  "Burgers",
  "Pizzas",
  "Lechon",
  "BBQ",
  "Chicken wings",
  "Snacks",
  "Coffee",
];

export default function VendorFormDialog({
  open,
  onClose,
  onSubmit,
  form,
  setForm,
  editingId,
  loading,
  isMerchant,
  isValid,
  onOpenLocationBrowse,
  onOpenPhotoDialog,
}) {
  const handleChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const handleToggle = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.checked }));
  const handleWeekdayToggle = (weekday) => (event) =>
    setForm((prev) => ({
      ...prev,
      weekdays: {
        ...(prev.weekdays ?? {}),
        [weekday]: event.target.checked,
      },
    }));

  const tagValues = form.tag
    ? form.tag
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={editingId ? "Edit vendor" : "Add vendor"}
      subtitle={
        editingId
          ? "Update the vendor's details."
          : "Create a new vendor and link it to listings later."
      }
      onSubmit={onSubmit}
      submitting={loading}
      submitLabel={editingId ? "Update vendor" : "Save vendor"}
      disableSubmit={!isValid}
      maxWidth="md"
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormField label="Vendor name" required>
            <TextField
              size="small"
              fullWidth
              required
              value={form.name}
              onChange={handleChange("name")}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormField label="Contact number" helper="Optional">
            <TextField
              size="small"
              fullWidth
              value={form.contactNumber}
              onChange={handleChange("contactNumber")}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormField label="Address" required>
            <TextField
              size="small"
              fullWidth
              required
              value={form.address}
              onChange={handleChange("address")}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormField label="Longitude" required>
            <TextField
              size="small"
              fullWidth
              required
              value={form.longitude}
              onChange={handleChange("longitude")}
              disabled={isMerchant}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormField label="Latitude" required>
            <TextField
              size="small"
              fullWidth
              required
              value={form.latitude}
              onChange={handleChange("latitude")}
              disabled={isMerchant}
            />
          </FormField>
        </Grid>

        {!isMerchant && (
          <Grid size={{ xs: 12 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={onOpenLocationBrowse}
            >
              Browse locations
            </Button>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 4 }}>
          <FormField label="Status">
            <FormControl fullWidth size="small">
              <Select value={form.status} onChange={handleChange("status")}>
                {VENDOR_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>
        </Grid>
        {!isMerchant && (
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField label="Category">
              <Autocomplete
                freeSolo
                options={VENDOR_CATEGORIES}
                value={form.category}
                onInputChange={(_, newValue) =>
                  setForm((prev) => ({ ...prev, category: newValue }))
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" />
                )}
              />
            </FormField>
          </Grid>
        )}
        <Grid size={{ xs: 6, md: 2 }}>
          <FormField label="Opens">
            <TextField
              size="small"
              fullWidth
              type="time"
              value={form.openTime}
              onChange={handleChange("openTime")}
              InputLabelProps={{ shrink: true }}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormField label="Closes">
            <TextField
              size="small"
              fullWidth
              type="time"
              value={form.closeTime}
              onChange={handleChange("closeTime")}
              InputLabelProps={{ shrink: true }}
            />
          </FormField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormField label="Open days">
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {WEEKDAY_OPTIONS.map((day) => (
                <FormControlLabel
                  key={day.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(form.weekdays?.[day.key])}
                      onChange={handleWeekdayToggle(day.key)}
                    />
                  }
                  label={day.label}
                />
              ))}
            </Stack>
          </FormField>
        </Grid>

        {!isMerchant && (
          <Grid size={{ xs: 12 }}>
            <FormField label="Notes">
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={form.notes}
                onChange={handleChange("notes")}
              />
            </FormField>
          </Grid>
        )}
        {!isMerchant && (
          <Grid size={{ xs: 12 }}>
            <FormField label="Tags">
              <Autocomplete
                multiple
                options={TAG_OPTIONS}
                value={tagValues}
                onChange={(_, newValue) =>
                  setForm((prev) => ({ ...prev, tag: newValue.join(", ") }))
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Add tags…" />
                )}
              />
            </FormField>
          </Grid>
        )}

        <Grid size={{ xs: 12, sm: 6 }}>
          <PhotoSlot
            label="Featured photo"
            value={form.featuredPhotoUrl}
            multi={false}
            onPick={() => onOpenPhotoDialog("featured")}
            onClear={() => setForm((prev) => ({ ...prev, featuredPhotoUrl: "" }))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PhotoSlot
            label="Thumbnails (up to 5)"
            values={form.thumbnailPhotoUrls}
            multi
            onPick={() => onOpenPhotoDialog("thumbnail")}
            onClear={() =>
              setForm((prev) => ({ ...prev, thumbnailPhotoUrls: [] }))
            }
          />
        </Grid>

        {!isMerchant && (
          <>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormField label="Service fee (%)">
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={form.serviceFeePercent}
                  onChange={handleChange("serviceFeePercent")}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormField label="Commission (%)">
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={form.commissionPercent}
                  onChange={handleChange("commissionPercent")}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </FormField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.isFeatured}
                      onChange={handleToggle("isFeatured")}
                    />
                  }
                  label="Featured"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.isPartner}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          isPartner: event.target.checked,
                          isNewPartner: event.target.checked
                            ? prev.isNewPartner
                            : false,
                        }))
                      }
                    />
                  }
                  label="Partner"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.isNewPartner)}
                      disabled={!form.isPartner}
                      onChange={handleToggle("isNewPartner")}
                    />
                  }
                  label={
                    <Chip
                      label="New partner"
                      size="small"
                      color={form.isNewPartner ? "primary" : "default"}
                      variant={form.isNewPartner ? "filled" : "outlined"}
                    />
                  }
                />
              </Stack>
            </Grid>
          </>
        )}
      </Grid>
    </FormDialog>
  );
}

function PhotoSlot({ label, value, values, multi, onPick, onClear }) {
  const hasContent = multi ? Array.isArray(values) && values.length > 0 : Boolean(value);
  const list = multi ? (values || []).slice(0, 5) : value ? [value] : [];

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, height: "100%" }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ textTransform: "none", display: "block", mb: 1 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: "100%",
          minHeight: 96,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "background.default",
          display: multi ? "grid" : "flex",
          gridTemplateColumns: multi ? "repeat(3, 1fr)" : undefined,
          gap: multi ? 0.5 : 0,
          p: multi ? 0.5 : 0,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hasContent ? (
          multi ? (
            list.map((url) => (
              <Box
                key={url}
                component="img"
                src={buildPhotoUrl(url)}
                alt={label}
                sx={{
                  width: "100%",
                  height: 64,
                  borderRadius: 0.75,
                  objectFit: "cover",
                }}
              />
            ))
          ) : (
            <Box
              component="img"
              src={buildPhotoUrl(value)}
              alt={label}
              sx={{
                width: "100%",
                height: 120,
                objectFit: "cover",
              }}
            />
          )
        ) : (
          <Typography variant="caption" color="text.secondary">
            No photo
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
        <Button size="small" variant="outlined" onClick={onPick}>
          {hasContent ? (multi ? "Edit" : "Replace") : multi ? "Select" : "Upload"}
        </Button>
        {hasContent && (
          <Button size="small" color="error" onClick={onClear}>
            {multi ? "Clear" : "Remove"}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
